import { loadStoredTokens } from "@/integrations/auth/tokens";
import { refreshTokens } from "@/integrations/auth/http";
import { apiOrigin, apiUrl } from "./apiBase";

export type EdgeInvokeError = {
  message: string;
  status?: number;
};

/**
 * Program API and other routes not under `/api` (e.g. `/program`).
 */
export function resolveEdgeFunctionUrl(functionPath: string): string {
  const base = apiOrigin();
  const fp = functionPath.startsWith("/") ? functionPath.slice(1) : functionPath;
  return base ? `${base}/${fp}` : `/${fp}`;
}

/**
 * Same shape as `supabase.functions.invoke` for drop-in replacement.
 * Uses the Neon JWT access token (Bearer only).
 */
export async function invokeEdgeFunction<T = any>(
  functionPath: string,
  options: {
    method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    body?: unknown;
    headers?: Record<string, string>;
  } = {}
): Promise<{ data: T | null; error: EdgeInvokeError | null }> {
  const { method = "GET", body, headers = {} } = options;

  const doFetch = async (
    authHeader: string | undefined
  ): Promise<{ data: T | null; error: EdgeInvokeError | null }> => {
    const url = resolveEdgeFunctionUrl(functionPath);
    const res = await fetch(url, {
      method,
      headers: {
        ...(body !== undefined && method !== "GET"
          ? { "Content-Type": "application/json" }
          : {}),
        ...(authHeader ? { Authorization: authHeader } : {}),
        ...headers,
      },
      body:
        body !== undefined && method !== "GET"
          ? JSON.stringify(body)
          : undefined,
    });

    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = { error: text || res.statusText };
    }

    if (!res.ok) {
      const p = parsed as Record<string, unknown>;
      const msg =
        (typeof p?.error === "string" && p.error) ||
        (typeof p?.message === "string" && p.message) ||
        res.statusText;
      return { data: null, error: { message: String(msg), status: res.status } };
    }

    return { data: parsed as T, error: null };
  };

  try {
    let access = loadStoredTokens().access;
    const authFromHeaders = headers.Authorization;
    let authHeader =
      authFromHeaders ||
      (access ? `Bearer ${access}` : undefined);

    let out = await doFetch(authHeader);

    if (
      out.error &&
      (out.error.status === 401 ||
        out.error.status === 403 ||
        out.error.status === 408)
    ) {
      const t = await refreshTokens();
      if (t) {
        out = await doFetch(`Bearer ${t.access_token}`);
      }
    }

    return out;
  } catch (e: unknown) {
    return {
      data: null,
      error: { message: e instanceof Error ? e.message : String(e) },
    };
  }
}

export type ApiInvokeError = EdgeInvokeError;

/**
 * Authenticated JSON calls under `/api/*` (events, team-invitations, storage, etc.).
 */
export async function invokeApi<T = any>(
  apiPath: string,
  options: {
    method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    body?: unknown;
    headers?: Record<string, string>;
    /** Skip Bearer (public endpoints). */
    anonymous?: boolean;
  } = {}
): Promise<{ data: T | null; error: ApiInvokeError | null }> {
  const {
    method = "GET",
    body,
    headers = {},
    anonymous = false,
  } = options;

  const path = apiPath.startsWith("/api/") ? apiPath : `/api/${apiPath.replace(/^\//, "")}`;

  const doFetch = async (
    authHeader: string | undefined
  ): Promise<{ data: T | null; error: ApiInvokeError | null }> => {
    const url = apiUrl(path);
    const res = await fetch(url, {
      method,
      headers: {
        ...(body !== undefined && method !== "GET"
          ? { "Content-Type": "application/json" }
          : {}),
        ...(authHeader ? { Authorization: authHeader } : {}),
        ...headers,
      },
      body:
        body !== undefined && method !== "GET"
          ? typeof body === "string"
            ? body
            : JSON.stringify(body)
          : undefined,
    });

    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = { error: text || res.statusText };
    }

    if (!res.ok) {
      const p = parsed as Record<string, unknown>;
      const msg =
        (typeof p?.error === "string" && p.error) ||
        (typeof p?.message === "string" && p.message) ||
        res.statusText;
      return { data: null, error: { message: String(msg), status: res.status } };
    }

    return { data: parsed as T, error: null };
  };

  try {
    if (anonymous) {
      return await doFetch(undefined);
    }
    let access = loadStoredTokens().access;
    let out = await doFetch(access ? `Bearer ${access}` : undefined);
    if (
      out.error &&
      (out.error.status === 401 || out.error.status === 403)
    ) {
      const t = await refreshTokens();
      if (t) {
        out = await doFetch(`Bearer ${t.access_token}`);
      }
    }
    return out;
  } catch (e: unknown) {
    return {
      data: null,
      error: { message: e instanceof Error ? e.message : String(e) },
    };
  }
}
